import { GroupShapeProps } from '../props/shape/group-shape-props';
import { GroupShapeStore } from '../store/group-shape-store';
import { GroupShapeMutationAPI } from '../api/group-shape-mutation-api';
import { GroupFrame } from '../utils/transform-utils';
import { BaseShapeDataModel } from './base-shape-data-model';
import type { PageContext } from '../context/page-context';
import type { IEventManager } from '../manager/event-manager';
import type { ChartDataModel, IChartDataModel } from './chart-data-model';
import type { TableDataModel, ITableDataModel } from './table-data-model';
import type { IGroupShapeData } from '../data';
import type { IGroupShapeProps } from '../props/shape/group-shape-props';
import type { IConnectorDataModel, ConnectorDataModel } from './connector-data-model';
import type { RestrictedDataModel } from './data-model';
import type { PageDataModel } from './page-data-model';
import type { IPictureDataModel, PictureDataModel } from './picture-data-model';
import type { IShapeDataModel, ShapeDataModel } from './shape-data-model';
import type { ISmartArtDataModel, SmartArtDataModel } from './smart-art-data-model';

export type DrawingShapeDataModel =
    | ShapeDataModel
    | GroupShapeDataModel
    | TableDataModel
    | ConnectorDataModel
    | PictureDataModel
    | ChartDataModel
    | SmartArtDataModel;

export type IDrawingShapeDataModel =
    | IShapeDataModel
    | IGroupShapeDataModel
    | ITableDataModel
    | IChartDataModel
    | IConnectorDataModel
    | IPictureDataModel
    | ISmartArtDataModel;

export type IPlaceholderShapeDataModel = IShapeDataModel | ITableDataModel | IPictureDataModel;

export class GroupShapeDataModel extends BaseShapeDataModel<IGroupShapeData> {
    public store: GroupShapeStore;
    public props: IGroupShapeProps;
    public api: GroupShapeMutationAPI;

    public get shapeList(): IDrawingShapeDataModel[] {
        return this.store.shapeList.toArray();
    }

    public constructor(
        data: IGroupShapeData,
        events: IEventManager,
        context: PageContext,
        public parent: GroupShapeDataModel | PageDataModel,
    ) {
        super(events, context);

        this.store = new GroupShapeStore(data, this, events, context);
        this.props = new GroupShapeProps(this.store.data, this.context);
        this.api = new GroupShapeMutationAPI(this, events);
    }

    /**
     * 以当前节点为根，遍历所有下级节点
     */
    public forEachDescendantShapes(
        callback: (
            child: IDrawingShapeDataModel,
            offset: number,
            parent: IGroupShapeDataModel,
            parentOffset: number,
        ) => void,
        startOffset = 0,
    ) {
        for (const [offset, shape] of this.shapeList.entries()) {
            if (shape instanceof GroupShapeDataModel) {
                shape.forEachDescendantShapes(callback, offset);
            } else {
                callback(shape, offset, this, startOffset);
            }
        }
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        this.forEachDescendantShapes(callback);
    }

    public getFrame(): GroupFrame {
        return GroupFrame.fromTransform(this.props.spPr?.xfrm ?? {});
    }

    public detach() {
        super.detach();
        for (const shape of this.store.shapeList.toArray()) {
            shape.detach();
        }
        return this;
    }
}

export { IGroupShapeProps };
export type IGroupShapeDataModel = RestrictedDataModel<GroupShapeDataModel>;
